import { inject, injectable } from 'inversify';

import { GoogleDriveFile, GoogleDriveSelectedFile } from '../models/google-drive-file';
import { Logger } from '../models/logger';
import { UIContext } from './ui-context';

declare var google;
declare var gapi;

@injectable()
export class GoogleDriverService {
    private readonly logger = Logger.getLogger('GoogleDriverService');

    readonly cid = '1002349272285-rb84b9urep0jghvnf5c04vfp2tf58quv.apps.googleusercontent.com';
    readonly scope = 'https://www.googleapis.com/auth/drive.file';
    readonly appId = '1002349272285';

    private token: string | null = null;
    private tokenClient: any = null;
    private pickerLoaded = false;
    private pendingCallback: ((files: GoogleDriveFile[]) => void) | null = null;

    constructor(
        @inject(UIContext) private uiContext: UIContext,
    ) {
    }

    showFileSelectDialog(callback: (googleDriveFiles: GoogleDriveFile[]) => void) {
        this.pendingCallback = callback;

        if (this.token && this.pickerLoaded) {
            this.showPicker();
            return;
        }

        this.initTokenClient();
        this.loadPickerApi();
        this.requestToken();
    }

    private initTokenClient() {
        if (this.tokenClient) return;
        this.tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: this.cid,
            scope: this.scope,
            callback: (response: any) => {
                if (response.error) {
                    this.logger.error({ message: `Google Drive auth error: ${response.error}` });
                    return;
                }
                this.token = response.access_token;
                this.tryShowPicker();
            },
            error_callback: (error: any) => {
                if (error.type === 'popup_blocked') {
                    this.uiContext.showErrorDialog({
                        title: 'Popup Blocked',
                        text: `Google Drive couldn't open the sign-in dialog because popups are blocked. Please enable popups and try again.`,
                    });
                } else {
                    this.logger.error({ message: `Google Drive token error: ${error.type || error.message}` });
                }
            },
        });
    }

    private loadPickerApi() {
        if (this.pickerLoaded) return;
        gapi.load('picker', () => {
            this.pickerLoaded = true;
            this.tryShowPicker();
        });
    }

    private requestToken() {
        if (this.token) {
            this.tryShowPicker();
            return;
        }
        this.tokenClient.requestAccessToken({ prompt: '' });
    }

    private tryShowPicker() {
        if (this.token && this.pickerLoaded) {
            this.showPicker();
        }
    }

    private showPicker() {
        const view = new google.picker.View(google.picker.ViewId.DOCS);
        const picker = new google.picker.PickerBuilder()
            .addView(view)
            .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
            .setOAuthToken(this.token)
            .setAppId(this.appId)
            .setCallback((data: any) => this.onDriveFileOpen(data))
            .build();
        picker.setVisible(true);
    }

    private onDriveFileOpen(data: any) {
        if (data.action === google.picker.Action.PICKED) {
            const files: GoogleDriveSelectedFile[] = data.docs;
            files.forEach(f => f.token = this.token);
            const gdFiles = GoogleDriveFile.createFromFileList(files);
            if (this.pendingCallback) {
                this.pendingCallback(gdFiles);
                this.pendingCallback = null;
            }
        } else if (data.action === google.picker.Action.CANCEL) {
            this.logger.trace7('Google Drive picker cancelled.');
            this.pendingCallback = null;
        }
    }
}
